import { Attack } from './attack.js'
import { Character } from './character.js'
import { MAPS } from './maps.js'
import { Terrain } from './terrain.js'
import { Utilities } from './utilities.js'

import { 
    ACTION_REST, ACTION_ATTACK, ACTION_MOVE,
    BOARD_HEIGHT, BOARD_WIDTH,
    COMBAT_DISTANCE_MELEE, COMBAT_DISTANCE_RANGED,
    DIRECTION_DOWN, DIRECTION_LEFT, DIRECTION_RIGHT, DIRECTION_UP, ALL_DIRECTIONS, 
    ENTITY_TYPE_CHARACTER, ENTITY_TYPE_TERRAIN,
    TERRAIN_PLAIN, TERRAIN_TREE, TERRAIN_ROCK, TERRAIN_WATER, 
}  from './constants.js'

class State {
    allCharacters() {
        return this.adversaries.concat(this.partiers)
    }

    allPartiersAreDead() {
        return this.partiers.findIndex(partier => partier.isAlive()) === -1
    }

    allAdversariesAreDead() {
        return this.adversaries.findIndex(adversary => adversary.isAlive()) === -1
    }

    characterWithInitiative() {
        const characterInOrder = this.initiative.order[this.initiative.indexWithInitiative]
        return this.allCharacters().find(character => 
            character.number === characterInOrder.number
        )
    }

    characterWithNumber(number) {
        return this.allCharacters().find(character => {
            return character.number === number
        } )
    }

    constructor() {
        this.action = null
        this.adversaries = []
        this.attacks = []
        this.direction = null
        this.ended = false
        this.initiative = {
            indexWithInitiative: 0,
            order: [],
        },
        this.log = []
        this.map = null
        this.partiers = []
        this.terrain = []
    }
}

export class Game {
    static mapList() {
        return MAPS
    }


    attack() {
        const character = this.state.characterWithInitiative()
        this.state.log.push(`${character.name} ATTACKED ${this.state.direction}`)
        
        // default to full board, reduce for melee
        let range = 12
        if(character.archetype.combatDistance === COMBAT_DISTANCE_MELEE) {
            range = 1
        }

        let adjusted = { x: character.position.x, y: character.position.y}
        adjusted = Utilities.adjustPosition(adjusted, this.state.direction)

        const attackTarget = this.state.map.actions.attack.find(it => {
            return it.direction === this.state.direction
        }).target

        // starting one tile away from the character, step through each tile in `direction`
        // while still in bounds and within `range` and make an attack object 
        let distance = 1
        while(Utilities.positionInBounds(adjusted) && distance <= range) {
            if(adjusted.x === attackTarget.position.x && adjusted.y === attackTarget.position.y) {
                distance = 10000 // break the loop after this attack
            }



            this.state.attacks.push(new Attack(
                character.archetype,
                adjusted,
            ))
            
            adjusted = Utilities.adjustPosition(adjusted, this.state.direction)
            distance++
        }



        if(Utilities.d(100) > 20) {
            const damage = Utilities.d(4)

            attackTarget.takeDamage(damage)
            this.state.log.push(`${attackTarget.name} IS DAMAGED FOR ${damage} POINTS`)

            if(attackTarget.isAlive()) {
                this.state.log.push(`${attackTarget.name} IS ${attackTarget.obfuscatedHealth()}`)
            } else {
                this.state.log.push(`${attackTarget.name} DIES`)
            }
            
            return true
        } else {
            this.state.log.push(`${character.name} MISSED ${attackTarget.name}`)
        }

        return false
    }

    anythingAtPosition(position) {
        const partier = Utilities.partierAtPosition(this.state.partiers, position) 
        const adversary = Utilities.adversaryAtPosition(this.state.adversaries, position) 
        const terrain = Utilities.terrainAtPosition(this.state.terrain, position)

        if(partier) return partier
        if(adversary) return adversary
        if(terrain) return terrain

        return false
    }

    chooseAdversaryAction() {
        // console.info('game.js - chooseAdversaryAction()')

        // By making it take a random number of tries before an action is selected
        // it makes it seem like the computer is taking time to decide
        const roll = Utilities.d(4)
        if(roll !== 1) {
            return
        }

        // if there's something to attack, attack it
        if(this.state.map.actions.attack.length > 0) {
            this.state.action = ACTION_ATTACK
            this.state.direction = Utilities.shuffle(this.state.map.actions.attack.map(
                attack => attack.direction
            )).pop()
        } else {
            this.state.action = ACTION_MOVE
            this.state.direction = this.chooseAdversaryDirection(this.state.map.actions.move.map(
                move => move.direction
            ))
        }
    }

    chooseAdversaryDirection(possibleDirections) {
        // console.log('game.js - chooseAdversaryDirection', possibleDirections)
        // get distances for each pc. huge value returned if dead so they're ignored
        const distances = this.state.partiers.map( (partier, partierIndex) => {
            const distance = partier.alive ? Math.hypot(
                partier.position.x - adversary.position.x,
                partier.position.y - adversary.position.y,
            ) : 1000
            return {
                pcIndex: partierIndex,
                distance,
            }
        // then sort distances so the shortest distance is first
        }).sort( (a, b) => {
            if(a.distance < b.distance) return -1
            if(a.distance > b.distance) return 1

            // must be equal
            return 0
        })

        const adversary = this.state.characterWithInitiative()
        // and figure out if one of the moves can get me closer to the
        // pcIndex of the closest pc by distance
        // todo shake chosen direction up a bit, going down first is predictable
        const firstPartier = this.state.partiers[distances[0].pcIndex]
        let log = `${adversary.name} MOVES `
        let direction = 'no direction picked'
        if(adversary.position.y < firstPartier.position.y - 1
            && possibleDirections.includes(DIRECTION_DOWN)

        ) {
            direction = DIRECTION_DOWN
            log = `${log}${DIRECTION_DOWN}`
        }
        
        else if(adversary.position.y > firstPartier.position.y + 1
            && possibleDirections.includes(DIRECTION_UP) 
        ) {
            direction = DIRECTION_UP
            log = `${log}${DIRECTION_UP}`
        }
        
        // am I lefter than the pc's x? if so move right
        else if(adversary.position.x < firstPartier.position.x
            && possibleDirections.includes(DIRECTION_RIGHT) 
        ) {
            direction = DIRECTION_RIGHT
            log = `${log}${DIRECTION_RIGHT}`
        }
        
        // am I righter than the pc's x? if so move left
        else if(adversary.position.x > firstPartier.position.x
            && possibleDirections.includes(DIRECTION_LEFT) 
        ) {
            direction = DIRECTION_LEFT
            log = `${log}${DIRECTION_LEFT}`
        }
        
        else {
            // something is blocking the direct path, so pick any 
            // allowed move to do something
            if(possibleDirections.length > 0) {
                const moveIndex = Utilities.d(possibleDirections.length) - 1
                direction =  possibleDirections[moveIndex]// Utilities.adjustPosition(adversary.position, moves[moveIndex])
            } else {
                // unless there really isn't anything that can be done
                log = `${log} nowhere. They are stuck!`
            }
        }

        this.state.log.push(log)
        return direction
    }

    constructor(settings) {
        // console.info('game.js - constructor', settings)
        this.settings = settings
        this.state = new State()
        this.state.log.push('Combat!')
        
        this.initializeMap(settings.map)

        // terrain has to come before characters so we don't put characters onto trees or rocks or water
        this.initializeTerrain(settings.terrain)

        this.initializeAdversaries(settings.adversaries)
        this.initializePartiers(settings.partiers)

        // roll initiative once characters are created
        if(this.state.initiative.order.length === 0) {          
            this.state.initiative.order = Utilities.shuffle(this.state.allCharacters()).map((character) => {
                return { 
                    isAdversary: character.isAdversary,
                    number: character.number,
                    name: character.name,
                } 
            })
            this.state.initiative.indexWithInitiative = 0
        } 
    }

    endGame() {
        // console.log('game.js - endGame')

        this.state.log.push('Game over!')
        this.state.map = null
        this.state.ended = true
    }

    endTurn() {
        this.state.action = null
        this.state.direction = null
        this.nextInitiative()
    }

    identifyActions() {
        // console.log('game.js - identifyInteractions') 

        const actions = {
            attack: [],
            move: [],
            rest: [],
        }

        const position = this.state.characterWithInitiative().position
        const range = this.state.characterWithInitiative().archetype.combatDistance
        ALL_DIRECTIONS.forEach( direction => {
            const attackTarget = this.identifyAttackTarget(position, direction, range)
            if(attackTarget) {
                actions.attack.push(attackTarget)
            }
            
            const moveTarget = this.identifyMoveTarget(position, direction, range)
            if(moveTarget) {
                actions.move.push(moveTarget)
            }

            actions.rest.push({
                position
            })
        })

        this.state.map.actions = actions
    }

    // exits early if there is any blocker, runs until it finds a target or is out of range or bounds
    identifyAttackTarget(startPosition, direction, range) {
        // console.log('game.js - identifyAttackTarget', startingPosition, direction)

        let adjusted = {
            x: startPosition.x,
            y: startPosition.y
        }
        adjusted = Utilities.adjustPosition(adjusted, direction)

        // starting one tile away from the character, step through each tile in `direction`
        // while still in bounds and within `range`
        let distance = 1
        while(Utilities.positionInBounds(adjusted) && distance <= range) {

            ///// vvvvv this section might be where a callback could occur?
            const tileType = this.state.map.board[adjusted.y][adjusted.x]
            // console.log('tileType at position', tileType)

            // if there is a rock or tree at this position they block any attacks (water does not)
            if(tileType === TERRAIN_ROCK || tileType == TERRAIN_TREE) {
                return false
            }

            // is there an opposition character at this position? 
            var target = false
            if (this.state.characterWithInitiative().isAdversary) {
                target = Utilities.partierAtPosition(this.state.partiers, adjusted)
            } else {
                target = Utilities.adversaryAtPosition(this.state.adversaries, adjusted)
            }

            if(target) {
                return {
                    direction,
                    position: adjusted, 
                    target
                }
            }
            ///// ^^^^^ this section might be where a callback could occur?

            // this tile has no character or blocking terrain, move to next tile
            adjusted = Utilities.adjustPosition(adjusted, direction)
            distance++
        }
        
        // went out of bounds or to maximum range without finding a target
        return false
    }


    identifyMoveTarget(startPosition, direction) {
        // console.log('game.js - identifyMoveTarget', startPosition, direction)

        let adjusted = {
            x: startPosition.x,
            y: startPosition.y
        }
        adjusted = Utilities.adjustPosition(adjusted, direction)

        // are we still on the map?
        if(!Utilities.positionInBounds(adjusted)) {
            return false
        }

        // is there is a rock or tree or water blocking us?
        if(this.state.map.board[adjusted.y][adjusted.x] != TERRAIN_PLAIN) {
            return false
        }

        // is there a character at this position blocking us? 
        if(Utilities.partierAtPosition(this.state.partiers, adjusted)) {
            return false
        }

        if(Utilities.adversaryAtPosition(this.state.adversaries, adjusted)) {
                return false
        }
         
        return {
            direction,
            position: adjusted
        }
    }

    initializeAdversaries(adversaries) {
        const boundary = {
            topLeft: {x:0,y:0}, 
            bottomRight: {x:10,y:4},
        }

        this.initializeCharacters(adversaries, this.state.adversaries, {isAdversary:true, boundary})
    }

    initializeCharacters(characters, collection, options) {
        // console.info('game.js - intializeCharacters', characters)
        if(Array.isArray(characters)) {
            this.characters = characters
            return
        }

        let count
        if(characters === 'random') {
            count = Utilities.d(4)
        } else {
            count = characters
        }

        for(let characterIndex = 0; characterIndex < count; characterIndex++) {
            options.position = this.safeSpawnPosition(
                options.boundary.topLeft, 
                options.boundary.bottomRight,
            )

            collection.push(new Character(options))
        }
    }

    initializeMap(map) {
        if(map === 'random') {
            this.state.map = MAPS[Utilities.d(MAPS.length) - 1]
        } else {
            this.state.map = MAPS.find(mapOption => mapOption.name === map)
        }
    }

    initializeTerrain() {
        const board = this.state.map.board

        for (let y = 0; y < board.length; y++) {
            for (let x = 0; x < board[0].length; x++) {
                switch(board[y][x]) {
                    case TERRAIN_TREE:
                        this.state.terrain.push(Terrain.Tree(x, y))
                        break

                    case TERRAIN_ROCK:
                        this.state.terrain.push(Terrain.Rock(x, y))                        
                        break

                    case TERRAIN_WATER:
                        this.state.terrain.push(Terrain.Water(x, y))
                        break

                    case TERRAIN_PLAIN:
                        break
                }
            }
        }
    }

    initializePartiers(partiers) {
        const boundary = {
            topLeft: {x:0,y:6}, 
            bottomRight: {x:10,y:10},
        }

        this.initializeCharacters(partiers, this.state.partiers, {isAdversary:false, boundary})
    }

    move() {
        const character = this.state.characterWithInitiative()
        let target = { x: character.position.x, y: character.position.y}
        target = Utilities.adjustPosition(character.position, this.state.direction)
        
        if(!Utilities.positionInBounds(target)) {
            console.error("TRIED TO MOVE OFF BOARD put something in log")
            return false
        }
        
        if(this.anythingAtPosition(target)) {
            return false
        }
        
        character.position.x = target.x
        character.position.y = target.y
        
        return true
    }

    nextInitiative() {
        if(this.state.allAdversariesAreDead() || this.state.allPartiersAreDead()) {
            console.log('ni')
            this.endGame()
            return
        }

        const initiative = this.state.initiative

        initiative.indexWithInitiative++
        if(initiative.indexWithInitiative >= initiative.order.length) {
            initiative.indexWithInitiative = 0
        }
        
        // skip any dead characters
        while(this.state.ended === false && !this.state.characterWithInitiative().isAlive()) {
            initiative.indexWithInitiative++
            if(initiative.indexWithInitiative >= initiative.order.length) {
                initiative.indexWithInitiative = 0
            }   
        }
    }

    options() {
        const options = {
            attacks: [],
            moves: [],
        }
        
        ALL_DIRECTIONS.forEach( direction => {
            const attackTarget = this.targetInDirection(direction)
            if( attackTarget ) {
                options.attacks.push(direction)
            } else {
                const moveTarget = Utilities.adjustPosition(
                    this.state.characterWithInitiative().position, direction
                )
                if (
                    !this.anythingAtPosition(moveTarget)
                    && Utilities.positionInBounds(moveTarget)
                ) { 
                    options.moves.push(direction)
                }
            }
        })

        return options
    }

    performAction() {
        // console.log('game.js - performAction', this.state.action, this.state.direction)

        const character = this.state.characterWithInitiative()

        if(this.state.action === ACTION_REST) {
           this.state.log.push(`${character.name} RESTED`)

            this.endTurn()
           return
        }

        if(this.state.action === null || this.state.direction === null) {
            return
        }

        if(this.state.action === ACTION_ATTACK) {
            this.attack()
        }

        if(this.state.action === ACTION_MOVE) {
            this.state.log.push(`${character.name} MOVED ${this.state.direction}`)
            this.move()
        }


        // once the action is performed the character's turn ends
        this.endTurn()
    }

    safeSpawnPosition = (topLeft = {x:0,y:0}, bottomRight = {x:10,y:10}, position = null) => {
        var potentialPosition = position ? position : Utilities.randomPosition(topLeft, bottomRight)

        while(this.anythingAtPosition(potentialPosition)) {
            potentialPosition = Utilities.randomPosition(topLeft, bottomRight)
        }

        return potentialPosition
    }

    setAction(action) {
        this.state.action = action
    }

    setDirection(direction) {
        this.state.direction = direction
    }

    targetInDirection(direction) {
        const character = this.state.characterWithInitiative()
        
        let targetPosition = this.partierInDirection(character.position, direction)
        
        let blockerPosition = this.terrainInDirection(character.position, direction)
        
        if(!targetPosition) return false
        
        const range = character.archetype.combatDistance
        if(direction === DIRECTION_LEFT) {
            if (blockerPosition && blockerPosition.x > targetPosition.x) return false
            if ((character.position.x - targetPosition.x) > range) return false
        }
        
        if(direction === DIRECTION_RIGHT) {
            if (blockerPosition.x < targetPosition.x) return false
            if ((targetPosition.x - character.position.x) > range) return false
        }
        
        if(direction === DIRECTION_DOWN) {
            if (blockerPosition.y < targetPosition.y) return false
            if ((targetPosition.y - character.position.y) > range) return false
        }
        
        if(direction === DIRECTION_UP) {
            if (blockerPosition.y > targetPosition.y) return false
            if ((character.position.y - targetPosition.y) > range) return false
        }

        return true
    }

    partierInDirection = (position, direction) => {
        const range = this.state.characterWithInitiative().archetype.combatDistance

        let adjusted = {
            x: position.x,
            y: position.y
        }

        let distance = 0
        while(Utilities.positionInBounds(adjusted) && distance <= range) {
            if(Utilities.partierAtPosition(this.state.partiers, adjusted)) {
                return adjusted
            }


            adjusted = Utilities.adjustPosition(adjusted, direction)
            distance++
        }

        return false
    }

    terrainInDirection = (position, direction) => {
        const range = this.state.characterWithInitiative().archetype.combatDistance

        let adjusted = {
            x: position.x,
            y: position.y
        }

        let distance = 0
        while(Utilities.positionInBounds(adjusted) && distance <= range) {
            if(Utilities.terrainAtPosition(this.state.terrain, adjusted)) {
                return adjusted
            }

            adjusted = Utilities.adjustPosition(adjusted, direction)
            distance++
        }

        return false
    }

    tick() {
        // console.info('game.js - tick', this.state.characterWithInitiative().name)
        if(this.state.ended) return

        this.state.attacks = []

        this.identifyActions()

        if(this.state.characterWithInitiative().isAdversary) {
            this.chooseAdversaryAction()
        }

        this.performAction()
    }
}